import React from "react";
import { Link } from "react-router-dom";
import useSeo from "../hooks/useSeo";
import { Leaf, FileText, BarChart3, ShieldCheck, ArrowRight, Clock } from "lucide-react";
import { COMPANY } from "../lib/company";

export default function ESGPage() {
  useSeo({
    title: "ESG & bæredygtighed · EMA Kurer ApS",
    description:
      "EMA Kurer arbejder på dokumenteret CO₂e-rapportering pr. rute, kunde og måned. Vores audit-klare ESG-rapport er under opbygning.",
    path: "/esg",
  });

  return (
    <div className="bg-brand-bg2 min-h-screen" data-testid="esg-page">
      {/* Hero */}
      <section className="bg-brand-dark text-white py-16 lg:py-24 relative overflow-hidden grain">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-green-500/15 text-green-300 px-3 py-1.5 rounded-full mb-4">
            <Clock size={14} /> Kommer senere — under opbygning
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            ESG &amp; bæredygtighed
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-white/80 max-w-3xl leading-relaxed">
            Vi arbejder på at gøre EMA Kurer's miljøaftryk fuldt målbart og transparent.
            Vores ESG-modul vil tilbyde <b>dokumenteret CO₂e-beregning pr. rute, kunde og måned</b>
            {" "}— udviklet til at være <b>audit-klar</b> og brugbar i kundernes egne ESG-rapporter.
          </p>
        </div>
      </section>

      {/* What's coming */}
      <section className="py-12 lg:py-16 -mt-6 relative z-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="bg-white border border-brand p-7 sm:p-10">
            <div className="kicker mb-3">Det vi arbejder på</div>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl text-brand-dark leading-tight">
              Tre konkrete moduler — på vej.
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
              {[
                {
                  icon: BarChart3,
                  title: "CO₂e-beregning pr. forsendelse",
                  body: "Dokumenteret CO₂e-estimat pr. rute, beregnet ud fra kørselsdata, køretøjsklasse og rute-distance. Tilgængelig i B2B-portalen.",
                },
                {
                  icon: FileText,
                  title: "Audit-klar ESG-rapport",
                  body: "Månedlige og kvartalsvise rapporter, designet til at kunne integreres i kundens egen ESG-redegørelse uden behov for ekstra beregning.",
                },
                {
                  icon: ShieldCheck,
                  title: "Datakvalitet & metode",
                  body: "Klar beskrivelse af beregningsmetode, datakilder og forbehold — så kunder, revisorer og myndigheder kan validere tallene.",
                },
              ].map((m) => (
                <div key={m.title} className="border border-brand p-5">
                  <div className="w-10 h-10 bg-brand-red text-white flex items-center justify-center mb-4">
                    <m.icon size={18} strokeWidth={2} />
                  </div>
                  <div className="font-display font-semibold text-base text-brand-dark">{m.title}</div>
                  <div className="text-brand-text2 text-sm mt-2 leading-relaxed">{m.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our approach */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="bg-white border border-brand p-7 sm:p-10">
            <div className="kicker mb-3 flex items-center gap-2 text-green-700">
              <Leaf size={14} /> Vores tilgang
            </div>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl text-brand-dark leading-tight">
              Vi siger først noget, når vi kan dokumentere det.
            </h2>
            <p className="mt-4 text-brand-text2 leading-relaxed">
              EMA Kurer er en lokal kurérvirksomhed på Sjælland. Vi arbejder på at gøre vores miljøaftryk målbart og transparent, men vi <b>gør ingen miljømærkningspåstande</b>, før vores datagrundlag og beregningsmetode er verificeret.
            </p>
            <p className="mt-3 text-brand-text2 leading-relaxed">
              Vores fokus er at indsamle reelle driftsdata — kilometer, køretøjsklasser, leveringsantal — og bygge en CO₂e-rapport, som vi tør stå inde for, og som vores kunder kan stole på i deres egen ESG-rapportering.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="bg-brand-dark text-white p-7 sm:p-10">
            <div className="kicker text-brand-red mb-3">Tidsplan</div>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl leading-tight">
              Hvornår er det klart?
            </h2>
            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-brand-red text-white flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">1</span>
                <div>
                  <div className="font-display font-semibold">Datagrundlag</div>
                  <div className="text-white/70 text-sm mt-1">Vi indsamler i øjeblikket driftsdata fra alle ruter — kilometer, køretøjsklasser og leveringsmønstre — på tværs af vores B2B-portal.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-brand-red text-white flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">2</span>
                <div>
                  <div className="font-display font-semibold">Metode &amp; validering</div>
                  <div className="text-white/70 text-sm mt-1">Beregningsmetoden bliver gennemgået og dokumenteret. Vi tester den mod kendte standarder og forbereder den til revision.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-brand-red text-white flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">3</span>
                <div>
                  <div className="font-display font-semibold">Rapportering pr. rute, kunde og måned</div>
                  <div className="text-white/70 text-sm mt-1">CO₂e-rapporter tilgængelige direkte i B2B-portalen samt eksportérbare til PDF/Excel.</div>
                </div>
              </li>
            </ul>
            <p className="text-white/50 text-xs mt-8">
              Tidsplan er vejledende. Vi udgiver ESG-modulet, når datagrundlag og metode er verificeret — ikke før.
            </p>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 text-center max-w-3xl mx-auto px-5 sm:px-8">
        <p className="text-brand-text2">
          Vil du være med i pilot-fasen for ESG-rapportering, eller har du spørgsmål til vores tilgang?
          {" "}Skriv til <a href={`mailto:${COMPANY.email_general}`} className="text-brand-red underline">{COMPANY.email_general}</a>.
        </p>
        <div className="mt-6">
          <Link to="/kontakt" className="btn-primary" data-testid="esg-contact-cta">
            Kontakt os <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
